import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  LinearProgress,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useI18n } from '../../i18n';

export function RaffleSeriesInfo() {
  const { user } = useAuth();
  const { t, lang } = useI18n();
  const token = user?.api_token ? `Bearer ${user.api_token}` : '';
  const [series, setSeries] = useState(null);
  const [loading, setLoading] = useState(true);
  const [updatingGoal, setUpdatingGoal] = useState(false);
  const [goalValue, setGoalValue] = useState('');
  const [prizeName, setPrizeName] = useState('');
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const loadSeries = useCallback(async () => {
    if (!token) {
      setSeries(null);
      setGoalValue('');
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch('/api/v1/raffles/series', {
        method: 'GET',
        headers: { Authorization: token },
      });
      if (!res.ok) {
        throw new Error(t('Ошибка загрузки данных о розыгрыше'));
      }
      const data = await res.json();
      if (data.status !== 'success') {
        throw new Error(data.error || t('Ошибка загрузки данных о розыгрыше'));
      }
      const payload = data.data || null;
      setSeries(payload);
      setGoalValue(payload && payload.tickets_goal != null ? String(payload.tickets_goal) : '');
      setPrizeName(payload && payload.tickets_goal_name != null ? String(payload.tickets_goal_name) : '');
    } catch (e) {
      setSeries(null);
      setGoalValue('');
      setPrizeName('');
      setSnackbar({ open: true, message: e.message || t('Ошибка загрузки'), severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    loadSeries();
  }, [loadSeries]);

  const handleGoalUpdate = async () => {
    const normalized = Number(goalValue);
    if (!Number.isFinite(normalized) || !Number.isInteger(normalized) || normalized <= 0) {
      setSnackbar({ open: true, message: t('Цель должна быть положительным целым числом'), severity: 'error' });
      return;
    }

    if (!token) {
      setSnackbar({ open: true, message: t('Нет доступа для обновления'), severity: 'error' });
      return;
    }

    setUpdatingGoal(true);
    try {
      const res = await fetch('/api/v1/raffles/goal', {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token,
        },
        body: JSON.stringify({ tickets_goal: normalized, prize_name: prizeName.trim() }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.status !== 'success') {
        throw new Error(data.error || t('Ошибка обновления цели'));
      }
      const updated = data.data || null;
      if (updated) {
        setSeries(updated);
        setGoalValue(updated.tickets_goal != null ? String(updated.tickets_goal) : String(normalized));
        setPrizeName(updated.tickets_goal_name != null ? String(updated.tickets_goal_name) : '');
      }
      setSnackbar({ open: true, message: t('Цель обновлена'), severity: 'success' });
    } catch (e) {
      setSnackbar({ open: true, message: e.message || t('Ошибка обновления цели'), severity: 'error' });
    } finally {
      setUpdatingGoal(false);
    }
  };

  const formatInteger = value =>
    new Intl.NumberFormat(lang === 'ru' ? 'ru-RU' : 'en-US', { maximumFractionDigits: 0 }).format(Number(value || 0));

  const ticketsSold = Number(series?.total_tickets_sold ?? 0);
  const ticketsGoal = Number(series?.tickets_goal ?? 0);
  const progress = ticketsGoal > 0 ? Math.min(100, (ticketsSold / ticketsGoal) * 100) : 0;
  const normalizedGoalValue = Number(goalValue);
  const hasGoalInput = goalValue.trim() !== '' && Number.isFinite(normalizedGoalValue) && Number.isInteger(normalizedGoalValue) && normalizedGoalValue > 0;
  const goalChanged =
    series && series.tickets_goal != null
      ? normalizedGoalValue !== Number(series.tickets_goal)
      : goalValue.trim() !== '';
  const currentPrizeName = series?.tickets_goal_name ?? '';
  const prizeChanged = prizeName !== currentPrizeName;
  const isSubmitDisabled =
    updatingGoal || !series || !hasGoalInput || (!goalChanged && !prizeChanged);

  return (
    <Box sx={{ flex: '1 1 360px', minWidth: 320, display: 'flex', flexDirection: 'column' }}>
      <Paper sx={{ p: 3, borderRadius: 2, display: 'flex', flexDirection: 'column', flex: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{t('Текущий розыгрыш')}</Typography>
          <Button size="small" variant="outlined" onClick={loadSeries} disabled={loading}>
            {t('Обновить')}
          </Button>
        </Box>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress size={32} />
          </Box>
        ) : !series ? (
          <Typography sx={{ color: '#666' }}>{t('Данные о текущем розыгрыше недоступны.')}</Typography>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1 }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3 }}>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('Серия')}
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700 }}>{series.code || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('Статус')}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{series.status || '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('Продано билетов')}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{formatInteger(ticketsSold)}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('Цель по билетам')}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{ticketsGoal > 0 ? formatInteger(ticketsGoal) : '—'}</Typography>
              </Box>
              <Box>
                <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase' }}>
                  {t('Название приза')}
                </Typography>
                <Typography sx={{ fontWeight: 600 }}>{currentPrizeName || '—'}</Typography>
              </Box>
            </Box>

            {ticketsGoal > 0 && (
              <Box sx={{ mt: 3 }}>
                <Typography sx={{ fontWeight: 500, mb: 1 }}>
                  {t('Прогресс:')} {formatInteger(ticketsSold)} / {formatInteger(ticketsGoal)}
                </Typography>
                <LinearProgress variant="determinate" value={progress} sx={{ height: 10, borderRadius: 5 }} />
              </Box>
            )}

            <Divider sx={{ my: 3 }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              <TextField
                label={t('Новая цель по билетам')}
                value={goalValue}
                onChange={e => setGoalValue(e.target.value)}
                type="number"
                inputProps={{ min: 1 }}
                sx={{ minWidth: 220 }}
              />
              <TextField
                label={t('Название приза')}
                value={prizeName}
                onChange={e => setPrizeName(e.target.value)}
                sx={{ minWidth: 220 }}
              />
              <Button
                variant="contained"
                onClick={handleGoalUpdate}
                disabled={isSubmitDisabled}
                sx={{ fontWeight: 600 }}
              >
                {updatingGoal ? t('Сохранение...') : t('Обновить цель')}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert onClose={() => setSnackbar(s => ({ ...s, open: false }))} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

